import {
Pagination,
PaginationContent,
PaginationEllipsis,
PaginationItem,
PaginationLink,
PaginationNext,
PaginationPrevious,
} from "@/components/ui/pagination"
import {
Select,
SelectContent,
SelectItem,
SelectTrigger,
SelectValue,
} from "@/components/ui/select"

interface PaginationControlsProps {
  currentPage: number
  totalPages: number
  itemsPerPage: number
  totalItems: number
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  limits?: number[]
  disabled?: boolean
}

export function PaginationControls({
  currentPage,
  totalPages,
  itemsPerPage,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  totalItems,
  onPageChange,
  onLimitChange,
  limits = [10, 20, 30, 40, 50, 100],
  disabled
}: PaginationControlsProps) {
  // Logic to determine which page numbers to show
  const getPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 5

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Always show first page
      pages.push(1)

      let startPage = Math.max(2, currentPage - 1)
      let endPage = Math.min(totalPages - 1, currentPage + 1)

      // Adjust if near start
      if (currentPage <= 3) {
        endPage = 4
        if (endPage >= totalPages) endPage = totalPages - 1
      }

      // Adjust if near end
      if (currentPage >= totalPages - 2) {
        startPage = totalPages - 3
        if (startPage <= 1) startPage = 2
      }

      if (startPage > 2) {
        pages.push("ellipsis-start")
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i)
      }

      if (endPage < totalPages - 1) {
        pages.push("ellipsis-end")
      }

      // Always show last page
      pages.push(totalPages)
    }

    return pages
  }

  return (
    <div className="flex items-center gap-3">
      {/* itemsPerPage 0 = limit selector hidden. Comparison (not bare &&) so
          the falsy 0 can't leak into the DOM as a literal "0". */}
      {itemsPerPage > 0 && onLimitChange && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground whitespace-nowrap hidden sm:block">Rows per page</span>
          <Select
            value={itemsPerPage.toString()}
            onValueChange={(value) => onLimitChange(Number(value))}
            disabled={disabled}
          >
            <SelectTrigger className="h-8 w-[80px]">
              <SelectValue placeholder={itemsPerPage.toString()} />
            </SelectTrigger>
            <SelectContent side="top">
              {limits.map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <Pagination className="justify-end bg-transparent w-auto mx-0">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              href="#"
              onClick={(e) => {
                e.preventDefault()
                if (currentPage > 1 && !disabled) onPageChange(currentPage - 1)
              }}
              className={currentPage <= 1 || disabled ? "pointer-events-none opacity-50" : "cursor-pointer"}
            />
          </PaginationItem>

          {getPageNumbers().map((page, index) => (
            <PaginationItem key={index} className="hidden sm:inline-block">
              {page === "ellipsis-start" || page === "ellipsis-end" ? (
                <PaginationEllipsis />
              ) : (
                <PaginationLink
                  href="#"
                  isActive={currentPage === page}
                  onClick={(e) => {
                    e.preventDefault()
                    if (!disabled) onPageChange(page as number)
                  }}
                >
                  {page}
                </PaginationLink>
              )}
            </PaginationItem>
          ))}
          
          <PaginationItem className="sm:hidden text-xs px-2 font-medium">
             Page {currentPage} of {Math.max(1, totalPages)}
          </PaginationItem>

          <PaginationItem>
            <PaginationNext
              href="#"
              onClick={(e) => {
                e.preventDefault()
                if (currentPage < totalPages && !disabled) onPageChange(currentPage + 1)
              }}
              className={currentPage >= totalPages || disabled ? "pointer-events-none opacity-50" : "cursor-pointer"}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  )
}